// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The noise floor of a late-night arcade (M5-22, 07 §6).
//
// "Distant machine chimes, a bass hum, rain on glass, the occasional knocker from another bay. It
// is mixed under the player's own machine and ducks during their machine's speech callouts."
// (06 §4.)
//
// Generated, like everything else in this product. Three continuous beds and one occasional
// event, and the discipline is that **none of them is ever the thing you are listening to**. An
// arcade's noise floor is the sound you notice when it stops. Anything here that draws attention
// is a bug, which is why the beds are all narrow-band and dull and why the knocker is rare.
//
// **A room tone that loops audibly is worse than silence.** A two-second loop of rain is a
// two-second loop, and the ear finds it inside thirty seconds. The beds are eight seconds and
// seam-folded (`loopSeamless`), and the knocker is scheduled at irregular intervals rather than
// baked into a bed where it would recur exactly every eight seconds for ever.
//
// **It ducks, it does not stop.** A callout is speech and speech has to be intelligible; cutting
// the room out from under it would be far more noticeable than pulling it down 9 dB for the
// length of the line.

import {
  addNoiseBurst,
  addPartial,
  addPitchDrop,
  Biquad,
  filterInPlace,
  loopSeamless,
  normalise,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'
import { PinkNoise } from './rolling'
import { LoopVoice } from './loopVoice'
import type { AudioBufferLike, AudioNodeLike, AudioPlaybackContext } from './webAudio'
import { Rng } from '@core/rng/Rng'
import { noiseFrom } from './solenoids'
import { knockerBay, knockerGapScale, roomFrom, type BaySound } from './bayAmbience'

/**
 * How long each continuous bed is, seconds.
 *
 * Eight. Long enough that the ear cannot hold the whole thing and find the seam, short enough
 * that three of them cost about a megabyte of float samples at 48 kHz — which is the real
 * constraint on a headset.
 */
export const BED_SECONDS = 8

/** Seam fold length, ms. Long, because these are all slow textures with no transients to smear. */
const SEAM_MS = 250

/**
 * The beds, in the order they sit in the mix.
 *
 * `hum` is the mains-and-transformer floor every arcade has and is the loudest of the three,
 * which is counter-intuitive until you stand in one: the bass is what makes a room feel like a
 * room, and the chimes are what make it feel like an arcade.
 */
export type AmbienceBed = 'hum' | 'chimes' | 'rain'

export const AMBIENCE_BEDS: readonly AmbienceBed[] = ['hum', 'chimes', 'rain']

/** Base level of each bed, relative to the ambience bus. */
export const BED_LEVELS: Readonly<Record<AmbienceBed, number>> = {
  hum: 0.55,
  chimes: 0.22,
  // 0.3 until 14 Sep 2026: "there is constant static noise in VR mode". A 2.4 kHz band of pink
  // noise through a headset's small speakers is a hiss, not weather, at any level that can be
  // heard over the hum; pulled down to where it colours the room rather than reads as a fault.
  // The bed's own spectrum is fixture-held with the native client and untouched. **Muted at 0.12
  // too** (15 Sep 2026: "static sound still in VR mode") — the bed comes back when it has a
  // spectrum that reads as weather on a headset, in both stacks at once.
  rain: 0,
}

/** How far the whole bed is pulled down while the machine is speaking. −9 dB. */
export const DUCK_LEVEL = 0.355

/** How quickly it ducks and recovers, seconds. Fast down, slow up — the standard shape. */
export const DUCK_ATTACK = 0.08
export const DUCK_RELEASE = 0.7

/**
 * A distant transformer hum plus the room's own bass.
 *
 * 50 Hz and two harmonics. **Every frequency completes a whole number of cycles in eight
 * seconds** — 50, 100, 150 and 25 Hz all do — because `loopSeamless` folds the *waveform* and
 * cannot fix an envelope discontinuity, and a partial left mid-cycle at the seam is a click once
 * per lap. That constraint is why these are round numbers rather than the detuned ones a beating
 * hum would want; the beating comes from the 25 Hz rumble instead, which is slow enough to read
 * as a building rather than as a chord.
 */
export function renderHum(out: Samples, sampleRate: number): void {
  addPartial(out, sampleRate, 50, 0.5, 1e9)
  addPartial(out, sampleRate, 100, 0.22, 1e9)
  addPartial(out, sampleRate, 150, 0.09, 1e9)
  addPartial(out, sampleRate, 25, 0.3, 1e9)
  filterInPlace(out, new Biquad().setLowpass(320, 0.7, sampleRate), true)
}

/**
 * Chimes from machines you cannot see.
 *
 * Sparse, quiet and pitched high — a machine two bays away is heard as its bells and nothing
 * else, because everything below about 800 Hz has been absorbed by the carpet and by the bodies
 * in between. The gaps are irregular so the bed never reads as a rhythm.
 *
 * Each chime is a decaying partial written into the buffer at an offset, which `addPartial`
 * cannot do on its own — so they are rendered into a scratch buffer and mixed in at the offset.
 * That is three lines and avoids giving `addPartial` a start parameter that only this caller
 * would ever use.
 */
export function renderChimes(out: Samples, sampleRate: number): void {
  const notes = [880, 1174.7, 1318.5, 1567.98, 2093]
  const scratch = new Float32Array(Math.round(sampleRate * 0.6))
  let at = 0.31
  let i = 0
  while (at < BED_SECONDS - 0.6) {
    scratch.fill(0)
    const frequency = notes[i % notes.length] as number
    addPartial(scratch, sampleRate, frequency, 0.10 + (i % 3) * 0.02, 220 + (i % 4) * 90)
    addPartial(scratch, sampleRate, frequency * 2.76, 0.03, 120)
    const start = Math.round(at * sampleRate)
    for (let s = 0; s < scratch.length && start + s < out.length; s++) {
      out[start + s]! += scratch[s] as number
    }
    at += 0.37 + ((i * 7) % 11) * 0.11
    i++
  }
  filterInPlace(out, new Biquad().setHighpass(700, 0.7, sampleRate), false)
}

/** Rain on the window: filtered noise with a slow swell, and nothing else. */
export function renderRain(out: Samples, sampleRate: number, noise: NoiseSource): void {
  const band = new Biquad().setBandpass(2400, 0.6, sampleRate)
  // A decay of effectively never, so the burst is the whole bed.
  addNoiseBurst(out, sampleRate, noise, band, 0.5, 1e9)
  // A gentle swell so it is weather rather than static. One whole cycle across the bed, so the
  // envelope meets itself at the seam.
  for (let i = 0; i < out.length; i++) {
    const phase = (i / out.length) * Math.PI * 2
    out[i] = (out[i] as number) * (0.75 + 0.25 * Math.sin(phase))
  }
}

/** The knocker from another bay — one hard mechanical crack, heard through a room. */
export function renderDistantKnocker(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(Math.round(sampleRate * 0.35))
  const crack = new Biquad().setBandpass(1800, 1.2, sampleRate)
  addNoiseBurst(out, sampleRate, noise, crack, 0.9, 24)
  addPitchDrop(out, sampleRate, 260, 90, 60, 0.5, 120)
  filterInPlace(out, new Biquad().setLowpass(1100, 0.8, sampleRate), true)
  normalise(out)
  return out
}

/** Render one bed, seam-folded so it loops without a click. */
export function renderBed(bed: AmbienceBed, sampleRate: number, noise: NoiseSource): Samples {
  const length = Math.max(2, Math.round(BED_SECONDS * sampleRate))
  const seam = Math.min(length - 1, samplesFor(SEAM_MS, sampleRate))
  const raw = new Float32Array(length + seam)
  if (bed === 'hum') renderHum(raw, sampleRate)
  else if (bed === 'chimes') renderChimes(raw, sampleRate)
  else renderRain(raw, sampleRate, new PinkNoise(noise))
  const out = loopSeamless(raw, length, seam)
  normalise(out)
  return out
}

/**
 * The gap before the next distant knocker, seconds.
 *
 * 25 to 95 seconds, from a caller-supplied uniform. Rare on purpose: a knocker is a *reward*
 * sound, and hearing one from another bay should read as somebody over there doing well — which
 * requires that it is not happening constantly. Anything under about twenty seconds turns the
 * room into a slot-machine floor.
 */
export function nextKnockerGap(unit: number): number {
  const clamped = Number.isFinite(unit) ? (unit < 0 ? 0 : unit > 1 ? 1 : unit) : 0.5
  return 25 + clamped * 70
}

/**
 * The ambience bus's level, given whether the machine is speaking.
 *
 * A one-pole approach with separate attack and release constants in seconds, so the caller can
 * run it from a frame time and get the same duck at any frame rate — the same reason
 * `poseTracking.ts` states its filter that way.
 */
export function duckedLevel(current: number, speaking: boolean, frameSeconds: number): number {
  const target = speaking ? DUCK_LEVEL : 1
  const tau = speaking ? DUCK_ATTACK : DUCK_RELEASE
  const k = frameSeconds <= 0 ? 0 : frameSeconds / (frameSeconds + tau)
  return current + (target - current) * k
}


// --- the player ------------------------------------------------------------------------------

/**
 * The arcade's noise floor, playing.
 *
 * Owned by whoever is in the VR venue and by nobody else: MR puts the machine in the player's own
 * room, where the room's noise floor is the room's, and adding a second one would be the single
 * most obviously wrong thing the product could do in passthrough.
 *
 * The knocker is scheduled from the frame clock rather than from `setTimeout`, for the same
 * reason `sessionSafety.ts` is: a timer keeps running while the session is hidden, and a headset
 * left on a desk for ten minutes would come back to a queue of knockers.
 */
export class ArcadeAmbience {
  private readonly voices = new Map<AmbienceBed, LoopVoice>()
  private readonly knocker: AudioBufferLike
  private readonly context: AudioPlaybackContext
  private readonly destination: AudioNodeLike
  private level = 1
  private untilKnocker: number
  private readonly rng: Rng
  /**
   * What the neighbouring machines sound like from where the player is standing (M8-21).
   *
   * Empty until a venue says otherwise, and empty is the arcade M5-22 shipped: a generated room
   * with nobody in it. In mixed reality it stays empty for ever, because there is no row of
   * machines in somebody's living room.
   */
  private room: readonly BaySound[] = []

  constructor(context: AudioPlaybackContext, destination: AudioNodeLike, seed = 0x41524341) {
    this.context = context
    this.destination = destination
    this.rng = new Rng(seed)
    for (const bed of AMBIENCE_BEDS) {
      const samples = renderBed(bed, context.sampleRate, noiseFrom(new Rng(seed ^ bed.length)))
      const buffer = context.createBuffer(1, samples.length, context.sampleRate)
      buffer.getChannelData(0).set(samples)
      this.voices.set(bed, new LoopVoice(context, buffer, destination, false))
    }
    const crack = renderDistantKnocker(context.sampleRate, noiseFrom(new Rng(seed ^ 0x4b)))
    this.knocker = context.createBuffer(1, crack.length, context.sampleRate)
    this.knocker.getChannelData(0).set(crack)
    this.untilKnocker = nextKnockerGap(this.rng.nextFloat())
  }

  /**
   * What the rest of the row is doing (M8-21).
   *
   * Handed in rather than worked out, because who is standing where is the venue's business and
   * this object has no idea there is a venue. Cheap to call every frame: it keeps a reference.
   */
  setRoom(sounds: readonly BaySound[]): void {
    this.room = sounds
  }

  /**
   * One frame. `speaking` ducks the whole bed under a callout (06 §4).
   *
   * `on` false stops every bed rather than muting it: a stopped `LoopVoice` costs one comparison
   * and a muted one costs three graph nodes and a resample, for the rest of the session.
   */
  advance(frameSeconds: number, speaking: boolean, on = true): void {
    if (!on) {
      for (const voice of this.voices.values()) voice.aim({ level: 0, pan: 0 })
      return
    }
    this.level = duckedLevel(this.level, speaking, frameSeconds)
    for (const [bed, voice] of this.voices) {
      // Panned apart so the room has width: the hum is everywhere, the chimes are down the row,
      // the rain is at the window. A mono room tone in a headset reads as a headphone effect.
      //
      // **The chimes are the one bed the room can move** (M8-21): they are the sound of other
      // machines, so when the venue knows which bays are being played they follow those. The hum
      // is the building and the rain is the window, and neither moves because somebody sat down.
      const heard = bed === 'chimes' && this.room.length > 0 ? roomFrom(this.room) : undefined
      const pan = heard?.pan ?? (bed === 'chimes' ? -0.4 : bed === 'rain' ? 0.6 : 0)
      voice.aim({ level: BED_LEVELS[bed] * this.level * (heard?.level ?? 1), pan })
    }

    this.untilKnocker -= frameSeconds
    if (this.untilKnocker > 0) return
    // A fuller arcade is a noisier one: five games in progress produce five times the replays.
    const busy = this.room.reduce((n, sound) => n + (sound.occupied ? 1 : 0), 0)
    this.untilKnocker = nextKnockerGap(this.rng.nextFloat()) * knockerGapScale(busy)
    this.fireKnocker()
  }

  dispose(): void {
    for (const voice of this.voices.values()) voice.stop()
    this.voices.clear()
  }

  /**
   * One crack from another bay.
   *
   * **From a bay somebody is playing, when the room knows of one** (M8-21) — a knocker is a replay
   * or an extra ball and an attract loop awards neither — at that bay's own distance and
   * direction. With no venue it falls back to M5-22's random pan, which is the right answer for a
   * room this client cannot see into.
   */
  private fireKnocker(): void {
    const from = knockerBay(this.room, this.rng.nextFloat())
    const source = this.context.createBufferSource()
    source.buffer = this.knocker
    const gain = this.context.createGain()
    gain.gain.value = 0.35 * this.level * (from?.level ?? 1)
    const panner = this.context.createStereoPanner()
    panner.pan.value = from?.pan ?? this.rng.nextSigned(0.8)
    source.connect(gain)
    gain.connect(panner)
    panner.connect(this.destination)
    source.start()
  }
}
